/**
 * Sitze Privacy-Lock 01.09.2026 — explizite Shares + Sitz-Ordner (PB-Client).
 * ACL bleibt PB listRule / Hooks. Explorer listet nur, was PB liefert.
 */

import { getPortalPocketBaseUrl } from '../../constants';
import {
  mapPbRecordToDocumentShare,
  type PortalDocumentShare,
} from './portalDocumentsPrivacy';

export {
  describePortalDocumentVisibility,
  mapPbRecordToDocumentShare,
  placeSharePopover,
  portalShareSeatLabel,
  SHARE_POPOVER_WIDTH_PX,
  shareTargetSeats,
  sharesForDocument,
  type PortalDocumentShare,
  type PortalDocumentShareAccess,
  type PortalDocumentVisibility,
  type PortalDocumentVisibilityPart,
} from './portalDocumentsPrivacy';

export const PORTAL_DOCUMENT_SHARES_COLLECTION = 'portal_document_shares';
export const PORTAL_FOLDERS_COLLECTION = 'portal_folders';

export interface PortalFolderRecord {
  id: string;
  customer_id: string;
  kanzlei_id: string;
  name: string;
  parent_id?: string;
  nc_path?: string;
}

type PbResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; status?: number };

function normalizePbAuthHeader(authToken: string): string {
  const t = authToken.trim();
  if (!t) return '';
  return t.toLowerCase().startsWith('bearer ') ? t : `Bearer ${t}`;
}

async function pbFetchJson<T>(
  authToken: string,
  path: string,
  init?: RequestInit,
): Promise<PbResult<T>> {
  const token = normalizePbAuthHeader(authToken);
  if (!token) return { ok: false, error: 'Nicht angemeldet — bitte neu einloggen.' };
  const pbUrl = getPortalPocketBaseUrl().replace(/\/$/, '');
  try {
    const res = await fetch(`${pbUrl}${path}`, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        Authorization: token,
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    if (!res.ok) {
      const msg =
        data && typeof data === 'object' && 'message' in data
          ? String((data as { message?: string }).message)
          : `HTTP ${res.status}`;
      return { ok: false, error: msg || `HTTP ${res.status}`, status: res.status };
    }
    return { ok: true, data: data as T };
  } catch {
    return { ok: false, error: 'Verbindung zu PocketBase fehlgeschlagen.' };
  }
}

function readStr(r: Record<string, unknown>, key: string): string {
  const v = r[key];
  return typeof v === 'string' ? v.trim() : '';
}

export function mapPbRecordToPortalFolder(r: Record<string, unknown>): PortalFolderRecord | null {
  const id = readStr(r, 'id') || (typeof r.id === 'string' ? r.id : String(r.id ?? ''));
  const name = readStr(r, 'name');
  const customer_id = readStr(r, 'customer_id');
  if (!id || !name || !customer_id) return null;
  const parent = readStr(r, 'parent_id');
  const nc = readStr(r, 'nc_path');
  return {
    id,
    customer_id,
    kanzlei_id: readStr(r, 'kanzlei_id'),
    name,
    parent_id: parent || undefined,
    nc_path: nc || undefined,
  };
}

/** PB listRule ist die ACL — kein zweites Filter im Client. 404 = Collection fehlt → leer. */
export async function listPortalDocumentShares(
  authToken: string,
): Promise<PortalDocumentShare[]> {
  const result = await pbFetchJson<{ items?: Record<string, unknown>[] }>(
    authToken,
    `/api/collections/${PORTAL_DOCUMENT_SHARES_COLLECTION}/records?sort=id&perPage=200`,
  );
  if (!result.ok) return [];
  return (result.data.items ?? [])
    .map(mapPbRecordToDocumentShare)
    .filter((x): x is PortalDocumentShare => Boolean(x));
}

export async function listPortalFolders(authToken: string): Promise<PortalFolderRecord[]> {
  const result = await pbFetchJson<{ items?: Record<string, unknown>[] }>(
    authToken,
    `/api/collections/${PORTAL_FOLDERS_COLLECTION}/records?sort=name&perPage=200`,
  );
  if (!result.ok) return [];
  return (result.data.items ?? [])
    .map(mapPbRecordToPortalFolder)
    .filter((x): x is PortalFolderRecord => Boolean(x));
}

export async function createPortalDocumentShare(
  authToken: string,
  input: { document_id: string; customer_id: string; kanzlei_id: string },
): Promise<PbResult<PortalDocumentShare>> {
  const document_id = input.document_id.trim();
  const customer_id = input.customer_id.trim();
  const kanzlei_id = input.kanzlei_id.trim();
  if (!document_id || !customer_id || !kanzlei_id) {
    return { ok: false, error: 'Dokument, Sitz und Kanzlei sind Pflicht.' };
  }
  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_DOCUMENT_SHARES_COLLECTION}/records`,
    {
      method: 'POST',
      body: JSON.stringify({
        document_id,
        customer_id,
        kanzlei_id,
        access: 'read',
      }),
    },
  );
  if (!result.ok) return result;
  const mapped = mapPbRecordToDocumentShare(result.data);
  if (!mapped) return { ok: false, error: 'Share angelegt, Antwort unvollständig.' };
  return { ok: true, data: mapped };
}

export async function deletePortalDocumentShare(
  authToken: string,
  shareId: string,
): Promise<PbResult<null>> {
  const id = shareId.trim();
  if (!id) return { ok: false, error: 'Share-Id fehlt.' };
  const result = await pbFetchJson<unknown>(
    authToken,
    `/api/collections/${PORTAL_DOCUMENT_SHARES_COLLECTION}/records/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
  if (!result.ok) return result;
  return { ok: true, data: null };
}

export async function ensurePortalFolderRecord(
  authToken: string,
  input: {
    name: string;
    kanzlei_id: string;
    customer_id: string;
    nc_path: string;
    parent_id?: string;
  },
): Promise<PbResult<PortalFolderRecord | null>> {
  const name = input.name.trim();
  const nc_path = input.nc_path.trim();
  if (!name || !nc_path) return { ok: false, error: 'Ordnername oder Pfad fehlt.' };
  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_FOLDERS_COLLECTION}/records`,
    {
      method: 'POST',
      body: JSON.stringify({
        name,
        kanzlei_id: input.kanzlei_id.trim(),
        customer_id: input.customer_id.trim(),
        nc_path: nc_path.endsWith('/') ? nc_path : `${nc_path}/`,
        parent_id: (input.parent_id || '').trim(),
      }),
    },
  );
  if (!result.ok) {
    // Unique-Index / schon da — Explorer listet den PB-Ordner trotzdem.
    if (result.status === 400) return { ok: true, data: null };
    return result;
  }
  return { ok: true, data: mapPbRecordToPortalFolder(result.data) };
}
